import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Plane, Mail, Lock, LogIn } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const staffResult = await login(email, password);

    if (staffResult.success) {
      navigate('/app', { replace: true });
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/client/auth/login`, {
        email,
        password
      });

      const { access_token, refresh_token, client: clientData } = response.data;

      localStorage.setItem('client_access_token', access_token);
      localStorage.setItem('client_refresh_token', refresh_token);
      localStorage.setItem('client_data', JSON.stringify(clientData));

      navigate('/client/dashboard', { replace: true });
      return;
    } catch (clientError) {
      setError('Invalid email or password');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 flex items-center justify-center px-4 sm:px-6 py-6 sm:py-8">
      <SEO
        title="Sign In — AeroTrick"
        description="Sign in to your AeroTrick account."
        canonical="/login"
        noindex={true}
      />

      {/* Background pattern - gardé identique */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}></div>
      </div>

      {/* Login Card - Responsive */}
      <div className="relative z-10 w-full max-w-[95%] sm:max-w-md">
        {/* Logo - Responsive */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center p-3 sm:p-4 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl sm:rounded-2xl shadow-xl mb-3 sm:mb-4">
            <Plane className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-wider">AEROTRICK</h1>
          <p className="text-blue-200 text-[10px] sm:text-sm mt-1 sm:mt-2 tracking-wide">AIRPORT MANAGEMENT SOLUTIONS</p>
        </div>

        {/* Login Form - Responsive */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl shadow-2xl p-5 sm:p-8 border border-white/20">
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 text-center">Welcome Back</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            <div>
              <label className="block text-blue-200 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-blue-300" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm sm:text-base placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder=""
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-blue-200 text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-blue-300" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm sm:text-base placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder=""
                  required
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-2 sm:p-3">
                <p className="text-red-200 text-xs sm:text-sm text-center">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold py-2.5 sm:py-3 rounded-lg transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
            >
              {loading ? (
                <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <LogIn className="w-4 h-4 sm:w-5 sm:h-5" />
                  Sign In
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer - Responsive */}
        <p className="text-center text-blue-300 text-[10px] sm:text-xs mt-6 sm:mt-8">
          © 2025 AEROTRICK - All rights reserved
        </p>
      </div>
    </div>
  );
};

export default Login;